import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';

export interface SkuArchiveModalProps {
  sku: InventorySKUListing | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sku: InventorySKUListing, isArchiving: boolean) => void;
  archivesProductName?: string | null;
}

export function SkuArchiveModal({
  sku,
  isOpen,
  onClose,
  onConfirm,
  archivesProductName = null,
}: SkuArchiveModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      title={`${sku?.isArchived ? 'Unarchive' : 'Archive'} SKU ${
        sku?.name ?? ''
      }?`}
    >
      {sku && (
        <div className='flex flex-col gap-6 p-4 text-gray-600'>
          {!sku.isArchived && (
            <>
              <p>You&apos;re about to archive {sku.name}.</p>
              <p className='max-w-2xl'>
                Archiving will remove this SKU from active inventory views, but
                all stock history, allocations, and in-transit data will remain
                securely stored in FLOX.
              </p>
              <p>You&apos;ll still be able to:</p>
              <ul className='list-disc ml-6'>
                <li>Access its inventory records and movements</li>
                <li>Track historical availability and allocations</li>
                <li>Restore it to active status at any time</li>
              </ul>
              {archivesProductName && (
                <p className='p-6 m-2 bg-amber-50 text-amber-800'>
                  <InformationCircleIcon className='inline h-5 w-5 mr-2' />
                  This is the last active SKU of {archivesProductName} —
                  archiving it archives the product too. Unarchiving any pack
                  size brings the product back.
                </p>
              )}
              <p className='p-6 m-2 mb-4 bg-sky-100 text-sky-800'>
                <InformationCircleIcon className='inline h-5 w-5 mr-2' />
                This action won&apos;t delete any data or external references.
              </p>
              <div className='flex flex-row gap-2 justify-center'>
                <Button
                  intent='secondary'
                  label='Cancel'
                  size='xl'
                  onClick={onClose}
                />
                <Button
                  intent='danger'
                  label='Archive SKU'
                  size='xl'
                  onClick={() => {
                    onConfirm(sku, true);
                    onClose();
                  }}
                />
              </div>
            </>
          )}
          {sku.isArchived && (
            <div className='flex flex-col gap-6 p-4 text-gray-600 max-w-2xl'>
              <p>You&apos;re about to unarchive {sku.name}.</p>
              <p>
                This action will restore this SKU to active inventory views,
                making it available for allocations and stock management.
              </p>
              <div className='flex flex-row gap-2 justify-center'>
                <Button
                  intent='secondary'
                  label='Cancel'
                  size='xl'
                  onClick={onClose}
                />
                <Button
                  intent='primary'
                  label='Unarchive SKU'
                  size='xl'
                  onClick={() => {
                    onConfirm(sku, false);
                    onClose();
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
